import { matchRoutes } from 'react-router';

/**
 * Central registry of the app's user-facing routes — the single source of
 * truth for turning a URL into a normalised route pattern and a human-readable
 * page name. Isomorphic (no server-only deps) so it can be used from the
 * Amplitude client plugin, the server-rendered `sitemap.txt`, SEO, etc.
 *
 * Keep this in sync with `app/routes.ts` — `route-registry.spec.ts` flattens
 * the React Router config and fails if a user-facing route is added, renamed,
 * or removed without updating this list.
 */
export type RouteEntry = {
  /** React Router path pattern, including the optional `:locale?` prefix. */
  pattern: string;
  /** Human-readable page name (analytics page name, breadcrumb, SEO title). */
  name: string;
};

export const ROUTES: RouteEntry[] = [
  { pattern: '/:locale?', name: 'Home' },
  {
    pattern: '/:locale?/experience/:experienceId',
    name: 'Experience details',
  },
  {
    pattern: '/:locale?/experience/:experienceId/book',
    name: 'Experience - Book',
  },
  { pattern: '/:locale?/upcoming-events', name: 'Upcoming events' },
  {
    pattern: '/:locale?/upcoming-events/book',
    name: 'Upcoming events - Book',
  },
  { pattern: '/:locale?/terms/:type?', name: 'Terms' },
  { pattern: '/:locale?/giftcards', name: 'Gift card' },
  {
    pattern: '/:locale?/giftcards/checkout',
    name: 'Gift card - Checkout',
  },
  { pattern: '/:locale?/punchcards', name: 'Punch card' },
  {
    pattern: '/:locale?/punchcards/checkout',
    name: 'Punch card - Checkout',
  },
  // Checkout. The entry URL, the gateway's cancel URL and the pre-separation
  // step URLs only redirect; the steps themselves live under a per-flow prefix. The info step
  // is the same page in both flows, so both patterns keep the same name.
  {
    pattern: '/:locale?/checkout/:experienceId',
    name: 'Checkout - Entry',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/cancel',
    name: 'Checkout - Payment cancelled',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/scheduled/date',
    name: 'Checkout - Scheduled - Date',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/scheduled/time',
    name: 'Checkout - Scheduled - Time',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/scheduled/info',
    name: 'Checkout - Complete booking',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/scheduled/info/edit',
    name: 'Checkout - Edit booking',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/open/tickets',
    name: 'Checkout - Open - Tickets',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/open/location',
    name: 'Checkout - Open - Location',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/open/date',
    name: 'Checkout - Open - Date',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/open/time',
    name: 'Checkout - Open - Time',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/open/info',
    name: 'Checkout - Complete booking',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/dates',
    name: 'Checkout - Legacy redirect',
  },
  {
    pattern: '/:locale?/checkout/:experienceId/info',
    name: 'Checkout - Legacy redirect',
  },
  { pattern: '/:locale?/payment/callback', name: 'Payment - Callback' },
  {
    pattern: '/:locale?/payment/giftcards-callback',
    name: 'Payment - Gift card callback',
  },
  {
    pattern: '/:locale?/payment/punchcards-callback',
    name: 'Payment - Punch card callback',
  },
  {
    pattern: '/:locale?/payment/booking-failed',
    name: 'Payment - Booking Failed',
  },
  { pattern: '/:locale?/payment/not-found', name: 'Payment - Not Found' },
  { pattern: '/:locale?/payment/not-payable', name: 'Payment - Not Payable' },
  { pattern: '/:locale?/payment/cancelled', name: 'Payment - Cancelled' },
  { pattern: '/:locale?/payment/:id', name: 'Payment - Payment link' },
  { pattern: '/:locale?/receipt/:id', name: 'Receipt' },
  { pattern: '/:locale?/review/:token', name: 'Write review' },
  { pattern: '/:locale?/giftcard/:id/refund', name: 'Gift card - Refund' },
];

const ROUTE_OBJECTS = ROUTES.map((r) => ({ path: r.pattern }));
const ENTRY_BY_PATTERN = new Map(ROUTES.map((r) => [r.pattern, r]));

// A locale-shaped leading segment (`en`, `en-US`). Used by the fallback to
// represent it as `:locale?` rather than mistaking it for a static segment.
const LOCALE_SEGMENT = /^[a-z]{2}(-[a-zA-Z]{2})?$/;

// An id-like segment: all-digits, or a uuid with or without hyphens (the
// hyphenless form is the 32-char experience id). Replaced with `:id` in the
// fallback so unknown routes still collapse to a low-cardinality pattern.
const ID_SEGMENT =
  /^(?:\d+|[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12})$/i;

export type RouteDescription = {
  /** Normalised route pattern (e.g. `/:locale?/experience/:experienceId`). */
  pattern: string;
  /** Human-readable page name (e.g. `Experience details`). */
  name: string;
  /** True when the path matched a registered route; false when derived by the
   *  fallback (e.g. a legacy `apps/web` path that shares the domain). */
  known: boolean;
};

/**
 * Describe a pathname: its normalised pattern + a human page name. Registered
 * routes get their curated name; anything else falls back to an id-collapsed
 * pattern and a humanised name, so unknown/legacy paths are still meaningful
 * and low-cardinality.
 */
export function describeRoute(rawPath: string): RouteDescription {
  const pathname = rawPath.split(/[?#]/)[0] || '/';

  const matched = matchRoutes(ROUTE_OBJECTS, pathname)?.[0];
  const knownPattern = matched?.route.path;
  // `:locale?` greedily captures any single segment, so e.g. `/terms-of-service`
  // would "match" `/:locale?` (home). Reject the match when the captured locale
  // isn't actually locale-shaped, so such paths fall through to the fallback.
  const localeParam = matched?.params?.locale;
  const localeValid = localeParam == null || LOCALE_SEGMENT.test(localeParam);
  if (knownPattern && localeValid) {
    const entry = ENTRY_BY_PATTERN.get(knownPattern);
    return {
      pattern: knownPattern,
      name: entry?.name ?? humanizePattern(knownPattern),
      known: true,
    };
  }

  const pattern = derivePattern(pathname);
  return {
    pattern,
    name: humanizePattern(pattern),
    known: false,
  };
}

/**
 * Whether react-router may navigate to `to` client-side.
 *
 * The domain is shared with the legacy `apps/web` app, so in-page links point
 * at paths this router doesn't serve (`/request`, `/upcoming-events`). A
 * client-side `<Link>` to one renders this app's 404 instead of letting the
 * microfrontend router hand the request over, so link components must fall
 * back to a document load for them. False also for another scheme or origin.
 */
export function isClientRoutablePath(to: string): boolean {
  if (/^[a-z][a-z\d+.-]*:/i.test(to) || to.startsWith('//')) return false;
  // Relative targets resolve against the current route, which this app owns.
  if (!to.startsWith('/')) return true;
  return describeRoute(to).known;
}

/** Collapse id-like segments to `:id` and a leading locale to `:locale?`. */
function derivePattern(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const parts: string[] = [];
  let start = 0;
  if (segments[0] && LOCALE_SEGMENT.test(segments[0])) {
    parts.push(':locale?');
    start = 1;
  }
  for (let i = start; i < segments.length; i++) {
    parts.push(ID_SEGMENT.test(segments[i]!) ? ':id' : segments[i]!);
  }
  return '/' + parts.join('/');
}

/** Build a page name from a pattern: drop params, title-case the static
 *  segments, join with " - "; the bare root is "Home". */
function humanizePattern(pattern: string): string {
  const words = pattern
    .split('/')
    .filter((s) => s && !s.startsWith(':'))
    .map((s) =>
      s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    );
  return words.length > 0 ? words.join(' - ') : 'Home';
}
